import { AlertCircle } from 'lucide-react'
import { useState } from 'react'
import type { EmailTemplate } from './types'

export default function TemplateFormModal({
  title,
  template,
  onSubmit,
  onClose,
  isLoading,
}: {
  title: string
  template?: EmailTemplate
  onSubmit: (data: any) => void
  onClose: () => void
  isLoading: boolean
}) {
  const [formData, setFormData] = useState({
    name: template?.name || '',
    description: template?.description || '',
    templateType: template?.templateType || 'promotional',
    category: template?.category || '',
    subject: template?.subject || '',
    htmlContent: template?.htmlContent || '',
    textContent: template?.textContent || '',
    isActive: template?.isActive ?? true,
    variables: template?.variables || [],
  })

  const [newVariable, setNewVariable] = useState({
    name: '',
    type: 'string',
    description: '',
    defaultValue: '',
  })

  const templateTypes = [
    { value: 'promotional', label: '프로모션' },
    { value: 'transactional', label: '트랜잭션' },
    { value: 'notification', label: '알림' },
    { value: 'newsletter', label: '뉴스레터' },
    { value: 'verification', label: '인증' },
    { value: 'welcome', label: '환영' },
    { value: 'reminder', label: '리마인더' },
    { value: 'other', label: '기타' },
  ]

  const variableTypes = [
    { value: 'string', label: '문자열' },
    { value: 'number', label: '숫자' },
    { value: 'date', label: '날짜' },
    { value: 'url', label: 'URL' },
    { value: 'email', label: '이메일' },
  ]

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name || !formData.subject || !formData.htmlContent) {
      alert('템플릿명, 제목, HTML 내용은 필수입니다.')
      return
    }

    // 시스템 템플릿 수정 시 확인
    if (template?.isSystem) {
      const confirmMsg = `이 템플릿은 시스템 템플릿입니다.\n\n수정 시 다음과 같은 영향이 있을 수 있습니다:\n• 사용자 인증 이메일에 영향\n• 알림 이메일 형식 변경\n• 시스템 전체 이메일 동작 변경\n\n정말 수정하시겠습니까?`

      if (!confirm(confirmMsg)) {
        return
      }
    }

    onSubmit({
      ...formData,
      textContent:
        formData.textContent || formData.htmlContent.replace(/<[^>]*>/g, ''),
    })
  }

  const addVariable = () => {
    if (!newVariable.name || !newVariable.type) {
      alert('변수명과 타입은 필수입니다.')
      return
    }

    setFormData(prev => ({
      ...prev,
      variables: [...prev.variables, { ...newVariable }],
    }))

    setNewVariable({
      name: '',
      type: 'string',
      description: '',
      defaultValue: '',
    })
  }

  const removeVariable = (index: number) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables.filter((_, i) => i !== index),
    }))
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white dark:bg-gray-800">
        <div className="border-b border-gray-200 p-6 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {title}
              </h3>
              {template?.isSystem && (
                <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
                  <div className="flex items-center">
                    <AlertCircle className="mr-2 h-4 w-4 text-amber-600 dark:text-amber-400" />
                    <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
                      시스템 템플릿 편집
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
                    이 템플릿은 시스템에서 자동으로 사용되는 템플릿입니다. 수정
                    시 이메일 발송에 영향을 줄 수 있으니 신중히 편집해주세요.
                  </p>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              ✕
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          {/* 기본 정보 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                템플릿명 *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={e =>
                  setFormData(prev => ({ ...prev, name: e.target.value }))
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                placeholder="템플릿명을 입력하세요"
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                템플릿 타입 *
              </label>
              <select
                value={formData.templateType}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    templateType: e.target.value,
                  }))
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                required
                disabled={template?.isSystem} // 시스템 템플릿은 타입 변경 불가
              >
                {templateTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {template?.isSystem && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                  시스템 템플릿의 타입은 변경할 수 없습니다.
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                카테고리
              </label>
              <input
                type="text"
                value={formData.category}
                onChange={e =>
                  setFormData(prev => ({ ...prev, category: e.target.value }))
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                placeholder="예: 마케팅, 시스템 등"
              />
            </div>

            <div className="flex items-center">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      isActive: e.target.checked,
                    }))
                  }
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  disabled={template?.isSystem} // 시스템 템플릿은 비활성화 불가
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  활성 상태
                </span>
              </label>
              {template?.isSystem && (
                <p className="ml-4 text-xs text-amber-600 dark:text-amber-400">
                  시스템 템플릿은 항상 활성 상태여야 합니다.
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              설명
            </label>
            <textarea
              value={formData.description}
              onChange={e =>
                setFormData(prev => ({ ...prev, description: e.target.value }))
              }
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder="템플릿에 대한 설명을 입력하세요"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              제목 *
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={e =>
                setFormData(prev => ({ ...prev, subject: e.target.value }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder="이메일 제목을 입력하세요"
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              HTML 내용 *
            </label>
            <textarea
              value={formData.htmlContent}
              onChange={e =>
                setFormData(prev => ({ ...prev, htmlContent: e.target.value }))
              }
              rows={12}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder="HTML 내용을 입력하세요. 변수는 {{변수명}} 형태로 사용하세요."
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              텍스트 내용
            </label>
            <textarea
              value={formData.textContent}
              onChange={e =>
                setFormData(prev => ({ ...prev, textContent: e.target.value }))
              }
              rows={6}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder="텍스트 버전 (선택사항, 비어있으면 HTML에서 자동 생성)"
            />
          </div>

          {/* 변수 관리 */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              변수 관리
            </label>

            {/* 기존 변수 목록 */}
            {formData.variables.length > 0 && (
              <div className="mb-4 space-y-2">
                {formData.variables.map((variable, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 rounded bg-gray-50 p-2 dark:bg-gray-700"
                  >
                    <span className="text-sm font-medium">{variable.name}</span>
                    <span className="text-xs text-gray-500">
                      ({variable.type})
                    </span>
                    {variable.description && (
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        - {variable.description}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => removeVariable(index)}
                      className="ml-auto text-xs text-red-600 hover:text-red-800"
                    >
                      삭제
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* 새 변수 추가 */}
            <div className="mb-2 grid grid-cols-2 gap-2 md:grid-cols-4">
              <input
                type="text"
                placeholder="변수명"
                value={newVariable.name}
                onChange={e =>
                  setNewVariable(prev => ({ ...prev, name: e.target.value }))
                }
                className="rounded border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <select
                value={newVariable.type}
                onChange={e =>
                  setNewVariable(prev => ({ ...prev, type: e.target.value }))
                }
                className="rounded border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                {variableTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="설명 (선택)"
                value={newVariable.description}
                onChange={e =>
                  setNewVariable(prev => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className="rounded border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <button
                type="button"
                onClick={addVariable}
                className="rounded bg-green-600 px-3 py-2 text-sm text-white hover:bg-green-700"
              >
                추가
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              HTML 내용에서 {'{{변수명}}'} 형태로 사용할 수 있습니다.
            </p>
          </div>

          {/* 액션 버튼 */}
          <div className="flex gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
            <button
              type="submit"
              disabled={isLoading}
              className={`rounded-lg px-6 py-2 font-medium text-white ${
                isLoading
                  ? 'cursor-not-allowed bg-gray-400'
                  : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'
              }`}
            >
              {isLoading ? '저장 중...' : template ? '수정' : '생성'}
            </button>

            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-6 py-2 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              취소
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
